import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Lock, AlertTriangle, CheckCircle2, Minus } from "lucide-react";
import { Card, PhaseBadge, Tooltip, ProgressBar } from "@/design/components";
import { useMoney } from "@/domain/hooks";
import { cn } from "@/design/cn";
import type { DealCardData, DealTodo } from "@/screens/shared/dealCards";

// ─── Health theme ─────────────────────────────────────────────────────────────
const HEALTH = {
  green: {
    border: "border-ok",
    dot: "bg-ok ring-ok/25",
    label: "On track",
    icon: CheckCircle2,
    iconCls: "text-ok",
  },
  amber: {
    border: "border-warn",
    dot: "bg-warn ring-warn/25",
    label: "Watch",
    icon: AlertTriangle,
    iconCls: "text-warnStrong",
  },
  red: {
    border: "border-bad",
    dot: "bg-bad ring-bad/25",
    label: "At risk",
    icon: AlertTriangle,
    iconCls: "text-bad",
  },
  grey: {
    border: "border-faint",
    dot: "bg-faint ring-faint/25",
    label: "No data",
    icon: Minus,
    iconCls: "text-faint",
  },
} as const;

type Rag = keyof typeof HEALTH;
function ragKey(v: string | undefined): Rag {
  return (v as Rag) in HEALTH ? (v as Rag) : "grey";
}

/** Compact age label: "26h" up to 48h, then "3d". */
function formatAge(hours: number): string {
  if (hours < 1) return "<1h";
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

/** Programme initials avatar — first 2 alpha chars of the name. */
function ProgrammeAvatar({ name }: { name: string }) {
  const letters = name.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase();
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#272FA0]/10 text-[11px] font-bold tracking-wide text-[#272FA0]">
      {letters}
    </span>
  );
}

function TodoRow({ todo }: { todo: DealTodo }) {
  const navigate = useNavigate();
  const go = (e: { stopPropagation: () => void; preventDefault: () => void }) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(todo.href);
  };
  return (
    <span
      role="link"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") go(e); }}
      className="focus-ring group -mx-1 flex cursor-pointer items-center gap-2.5 rounded-md px-1 py-1.5 hover:bg-subtle/60 transition-colors"
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-brand/10 text-[11px] font-bold tabular-nums text-brand">
        {todo.count}
      </span>
      <span className="text-sm text-mute">{todo.label}</span>
      {todo.ageHours != null && (
        <span className="ml-auto whitespace-nowrap tabular-nums text-[11px] text-faint">
          oldest {formatAge(todo.ageHours)}
        </span>
      )}
      <ChevronRight
        className={cn("h-3.5 w-3.5 shrink-0 text-faint transition-transform group-hover:translate-x-0.5", todo.ageHours == null && "ml-auto")}
        aria-hidden
      />
    </span>
  );
}

function DealCardInner({
  data,
  accessPending,
  disabled,
}: {
  data: DealCardData;
  accessPending?: boolean;
  disabled?: boolean;
}) {
  const money = useMoney();
  const health = HEALTH[ragKey(data.health)];
  const HealthIcon = health.icon;
  const spentPct = data.budget > 0 ? data.spent / data.budget : 0;

  return (
    <div className="flex h-full flex-col p-4">
      {/* ── Identity ─────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3">
        <ProgrammeAvatar name={data.name} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-1.5">
            <h3 className="text-sm font-semibold leading-snug text-ink">{data.name}</h3>
            <PhaseBadge phase={data.phase} />
          </div>
          <p className="mt-0.5 truncate text-xs text-faint">
            {data.funder}
            {data.yearSpan && (
              <> · <span className="tabular-nums">{data.yearSpan}</span></>
            )}
          </p>
        </div>
      </div>

      {accessPending ? (
        <div className="mt-4 flex-1 flex items-end">
          <p className="flex items-center gap-1.5 text-sm text-faint">
            <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Access pending — shared once feasibility is ready.
          </p>
        </div>
      ) : (
        <>
          <div className="my-3 h-px bg-hairline" />

          {/* ── Health status ─────────────────────────────────────────────── */}
          <div className="flex items-center gap-2">
            <span className={cn("h-2 w-2 rounded-full ring-4", health.dot)} />
            <HealthIcon className={cn("h-3.5 w-3.5", health.iconCls)} aria-hidden />
            <span className={cn("text-xs font-semibold", health.iconCls)}>{health.label}</span>
          </div>

          {/* ── Spend vs budget ───────────────────────────────────────────── */}
          <div className="mt-3 space-y-1.5">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="font-semibold tabular-nums text-ink">{money(data.spent)}</span>
              <span className="text-faint">
                of {money(data.budget)}
                {" · "}
                <span className="tabular-nums font-medium text-mute">
                  {Math.round(spentPct * 100)}%
                </span>
              </span>
            </div>
            <ProgressBar
              value={spentPct}
              tone={spentPct > 0.9 ? "bad" : spentPct > 0.75 ? "warn" : "ok"}
            />
          </div>

          {/* ── To-do list ───────────────────────────────────────────────── */}
          <div className="mt-3 flex-1">
            <div className="mb-2 h-px bg-hairline" />
            {data.todos.length > 0 ? (
              <div className="space-y-0.5">
                {data.todos.map((t) => (
                  <TodoRow key={t.key} todo={t} />
                ))}
              </div>
            ) : disabled ? (
              <p className="py-1 text-sm text-faint">In feasibility · workspace not yet configured</p>
            ) : (
              <p className="flex items-center gap-1.5 py-1 text-sm font-semibold text-ok">
                <CheckCircle2 className="h-3.5 w-3.5" /> All clear
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function DealCard({
  data,
  to,
  from,
  disabled,
  accessPending,
}: {
  data: DealCardData;
  to: string;
  from?: string;
  disabled?: boolean;
  accessPending?: boolean;
}) {
  const nonClickable = disabled || accessPending;
  const borderCls = cn("border-l-[3px]", HEALTH[ragKey(data.health)].border);

  if (nonClickable) {
    const reason = accessPending
      ? "Access pending — shared once the feasibility work is ready"
      : "In feasibility — workspace not yet configured";
    return (
      <Tooltip label={reason}>
        <Card aria-label={`${data.name}: ${reason}`} className={cn("h-full opacity-75", borderCls)}>
          <DealCardInner data={data} accessPending={accessPending} disabled={disabled} />
        </Card>
      </Tooltip>
    );
  }

  return (
    <Link
      to={to}
      state={from ? { from } : undefined}
      aria-label={`Open ${data.name}`}
      className="focus-ring group block h-full rounded-card"
    >
      <Card
        variant="raised"
        className={cn("h-full transition-shadow group-hover:shadow-lg", borderCls)}
      >
        <DealCardInner data={data} />
      </Card>
    </Link>
  );
}
